import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, getDeviceId } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DhunLogo, AivigoFooter } from "../components/DhunLogo";
import { Loader2, Sparkles, PartyPopper } from "lucide-react";
import { toast } from "sonner";

export default function PubWelcome() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPubSession, login } = useAuth();
  const [pub, setPub] = useState(null);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState("hero"); // hero | name
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const tableParam = searchParams.get("table");

  useEffect(() => {
    if (tableParam) {
      localStorage.setItem(`dd_table_${slug}`, tableParam);
    }
    api
      .get(`/pubs/${slug}`)
      .then(({ data }) => setPub(data))
      .catch(() => setError("Pub not found"));
  }, [slug, tableParam]);

  const onStart = () => {
    if (hasPubSession()) {
      navigate(`/pub/${slug}/home`);
    } else {
      setStage("name");
    }
  };

  const onJoin = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error("Please enter your name (min 2 chars)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer", { name: trimmed, slug, device_id: getDeviceId() });
      login(data.token, data.user);
      toast.success(`Hi, ${data.user.name}! Welcome`);
      navigate(`/pub/${slug}/home`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] text-white">
        <div className="text-center">
          <p className="text-xl font-semibold">Pub not found</p>
          <p className="mt-2 text-sm text-slate-400">Check the QR code and try again.</p>
        </div>
      </div>
    );
  }

  if (!pub) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-between overflow-hidden bg-[#0B0E14] px-6 py-10 text-white"
      data-testid="pub-welcome-screen"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.35) 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(236,72,153,0.25) 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute bottom-10 left-0 h-72 w-72 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)" }} />

      <div className="relative z-10 flex flex-col items-center gap-2 pt-6">
        <DhunLogo size={120} />
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-violet-300">
          Aivigo
        </p>
      </div>

      <div className="relative z-10 w-full max-w-md flex-1 pt-14 text-center">
        <div className="mb-4 inline-flex items-center gap-1 rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-200">
          <Sparkles className="h-3 w-3" />
          {tableParam ? `Table ${tableParam}` : "Live now"}
        </div>
        <h1
          className="font-black leading-tight tracking-tight"
          style={{ fontFamily: "'Outfit', sans-serif", fontSize: "clamp(2rem, 8vw, 3rem)" }}
          data-testid="welcome-pub-name"
        >
          Welcome to
          <br />
          <span style={{
            backgroundImage: "linear-gradient(90deg, #EC4899 0%, #8B5CF6 50%, #06B6D4 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>{pub.name}</span>
        </h1>
        <p className="mt-5 text-base text-slate-300">Request. Vote. Celebrate.</p>

        {stage === "name" && (
          <div className="mx-auto mt-8 max-w-sm space-y-3 rounded-2xl border border-white/10 bg-[#131822] p-5 text-left">
            <Label className="text-xs uppercase tracking-widest text-slate-400">
              Your name for the shoutouts
            </Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Rahul"
              data-testid="welcome-name-input"
              className="h-12 border-white/10 bg-black/30 text-white"
              onKeyDown={(e) => e.key === "Enter" && onJoin()}
            />
            <Button
              onClick={onJoin}
              disabled={loading}
              data-testid="welcome-join-button"
              className="h-12 w-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500 font-bold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PartyPopper className="mr-2 h-4 w-4" /> Join</>}
            </Button>
          </div>
        )}
      </div>

      <div className="relative z-10 w-full max-w-md pb-6">
        {stage === "hero" && (
          <Button
            onClick={onStart}
            data-testid="welcome-start-button"
            className="group relative w-full animate-pulse overflow-hidden py-9 text-2xl font-black tracking-wide text-white"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
              boxShadow: "0 0 40px rgba(139,92,246,0.55), 0 0 80px rgba(236,72,153,0.25)",
              borderRadius: 999,
            }}
          >
            Start
          </Button>
        )}
        <AivigoFooter className="mt-4" />
      </div>
    </div>
  );
}
